import { useContext, useMemo } from "react";
import { useRegisterActions } from "@refinedev/kbar";
import type { Action } from "kbar";
import { useSetLocale, useGetLocale } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useMarketplaceStore } from "../store/useMarketplaceStore";
import {
  useWorkspaceStore,
  OrderSnapshot,
  ProductSnapshot,
} from "../store/useWorkspaceStore";
import { ColorModeContext } from "../providers/colorProvider";

export const useKbarActions = () => {
  const { toggleMode, mode } = useContext(ColorModeContext);
  const setLocale = useSetLocale();
  const getLocale = useGetLocale();
  const currentLocale = getLocale();
  const navigate = useNavigate();

  const countries = useMarketplaceStore((s) => s.countries);
  const selectCountry = useMarketplaceStore((s) => s.selectCountry);

  const workspaceItems = useWorkspaceStore((s) => s.items);
  const openWorkspace = useWorkspaceStore((s) => s.open);
  const clearWorkspace = useWorkspaceStore((s) => s.clearAll);

  const staticActions = useMemo<Action[]>(
    () => [
      {
        id: "toggle-dark-mode",
        name: mode === "dark" ? "Açık Tema" : "Koyu Tema",
        section: "Ayarlar",
        keywords: "dark light tema mod theme",
        perform: toggleMode,
      },
      {
        id: "lang-tr",
        name: "Türkçe",
        subtitle: currentLocale === "tr" ? "Aktif" : undefined,
        section: "Dil",
        keywords: "turkish dil language tr türkçe",
        perform: () => setLocale?.("tr"),
      },
      {
        id: "lang-en",
        name: "English",
        subtitle: currentLocale === "en" ? "Active" : undefined,
        section: "Dil",
        keywords: "english dil language en ingilizce",
        perform: () => setLocale?.("en"),
      },
      {
        id: "workspace-open",
        name: "Workspace'i Aç",
        section: "Workspace",
        keywords: "workspace pin open aç panel",
        perform: openWorkspace,
      },
      {
        id: "workspace-clear",
        name: "Workspace'i Temizle",
        section: "Workspace",
        keywords: "workspace clear temizle sil kaldır",
        perform: clearWorkspace,
      },
    ],
    [mode, toggleMode, currentLocale, setLocale, openWorkspace, clearWorkspace],
  );

  const marketplaceActions = useMemo<Action[]>(
    () =>
      countries.map((country) => ({
        id: `marketplace-${country.countryCode}`,
        name: country.countryName,
        subtitle: country.storeName ?? country.name,
        section: "Mağaza",
        keywords: `${country.name} ${country.countryCode} ${country.countryName} mağaza store marketplace`,
        perform: () => selectCountry(country.countryCode),
      })),
    [countries, selectCountry],
  );

  const pinnedActions = useMemo<Action[]>(
    () =>
      workspaceItems
        .filter((i) => i.type !== "note")
        .map((item) => {
          if (item.type === "order") {
            const s = item.snapshot as OrderSnapshot;
            return {
              id: `pin-order-${item.uid}`,
              name: s.amazonOrderId,
              subtitle: s.itemName,
              section: "Pinli Siparişler",
              keywords: `order sipariş ${s.amazonOrderId} ${s.itemName ?? ""}`,
              perform: () => navigate(`/order-detail/${s.id}`),
            };
          }
          const s = item.snapshot as ProductSnapshot;
          return {
            id: `pin-product-${item.uid}`,
            name: s.asin,
            subtitle: s.itemName,
            section: "Pinli Ürünler",
            keywords: `product ürün ${s.asin} ${s.itemName ?? ""}`,
            perform: () => navigate(`/inventory/product/${s.asin}`),
          };
        }),
    [workspaceItems, navigate],
  );

  useRegisterActions(
    [...staticActions, ...marketplaceActions, ...pinnedActions],
    [staticActions, marketplaceActions, pinnedActions],
  );
};
